import React, { useState, useEffect } from 'react';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import AgreementCancelModal from './AgreementCancelModal';
import { get, omit, isEmpty } from 'lodash';

import AgreementPage1 from './pages/AgreementPage1';
import AgreementPage2 from './pages/AgreementPage2';
import AgreementPage3 from './pages/AgreementPage3';
import AgreementPage4 from './pages/AgreementPage4';
import AgreementPage5 from './pages/AgreementPage5';
import ErrorBoundary from '../../base/ErrorBoundary';
import Api from '../../api/Api';
import Validation from '../../utils/Validation';
import Urls from '../../definitions/Urls';
import SessionUtils from '../../utils/SessionUtils';
import { doExitHpa } from '../../data/hpa/actions';
import WindowUtils from '../../utils/WindowUtils';
import {APPLICATION, USER} from "../../data/reducer";
import {
  NAVIGATE_TO_AGREEMENT, POST_REGISTER_REDIRECT_URL_COXAPOLKU_VALUE,
  POST_REGISTER_REDIRECT_URL_KEY, POST_REGISTER_REDIRECT_URL_TAYSPOLKU_VALUE
} from "../../definitions/AgreementRedirectConstants";

const MyErrorBoundary = withRouter(ErrorBoundary);

const Agreement = (props) => {
  const { agreementUserInfo, isHpa, doExitHpa } = props;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [sendingAgreement, setSendingAgreement] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  const [phoneEdited, setPhoneEdited] = useState(null);
  const [emailEdited, setEmailEdited] = useState(null);
  const [errors, setErrors] = useState(null);

  const [userInfo, setUserInfo] = useState();
  const [navigateToAgreement, setNavigateToAgreement] = useState(false);

  const initialize = () => {
    //patient phone number will be fetched from uranus
    Api.getPatientContactDetails().then((data) => {
      let { phone, email } = data;
      setUserInfo({
        phone: phone.replace(/\s/g, ''),
        confirmPhone: phone.replace(/\s/g, ''),
        phoneFieldEdited: phone === '',
        noEmail: false,
        email: email,
        confirmEmail: email,
        emailFieldEdited: email === '',
      });
    });
  };


  if (props.location.pathname.includes("/coxapolku/redirect")) {
    if (!props.isUser && props.loggedIn && props.applicationState === "INITIALIZED") {
      if (WindowUtils.getFromSessionStorage(NAVIGATE_TO_AGREEMENT) == null) {
        if(!navigateToAgreement) {
          WindowUtils.addToSessionStorage(NAVIGATE_TO_AGREEMENT, "true");
          WindowUtils.addToSessionStorage(POST_REGISTER_REDIRECT_URL_KEY, POST_REGISTER_REDIRECT_URL_COXAPOLKU_VALUE);
          setNavigateToAgreement(true);
        }
      }
    }
  }

  if (props.location.pathname.includes("/tayspolku/redirect")) {
    if (!props.isUser && props.loggedIn && props.applicationState === "INITIALIZED") {
      if (WindowUtils.getFromSessionStorage(NAVIGATE_TO_AGREEMENT) == null) {
        if(!navigateToAgreement) {
          WindowUtils.addToSessionStorage(NAVIGATE_TO_AGREEMENT, "true");
          WindowUtils.addToSessionStorage(POST_REGISTER_REDIRECT_URL_KEY, POST_REGISTER_REDIRECT_URL_TAYSPOLKU_VALUE);
          setNavigateToAgreement(true);
        }
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    initialize();
  }, [agreementUserInfo]);

  useEffect(() => {
    if(navigateToAgreement) {
      const agreementStatus = WindowUtils.getFromSessionStorage(NAVIGATE_TO_AGREEMENT);
      if (agreementStatus != null && agreementStatus === "true") {
        WindowUtils.navigate('/etusivu');
      }
    }
  }, [navigateToAgreement]);

  const cancelAgreement = () => {
    /* log event to backend, and redirect to logout (logout independent of success...)
     *  also current path is sent as payload as required.
     * */
    let page = get(this, 'props.location.pathname', null);

    if (page !== null) {
      page = page.replace('/', '');
    }

    Api.denyUserAgreementOnRegistration({ payload: page })
      .then(() => redirectAfterCancelAgreement())
      .catch(() => redirectAfterCancelAgreement());
  };

  const redirectAfterCancelAgreement = () => {
    if (isHpa) {
      doExitHpa();
    } else {
      SessionUtils.logout();
    }
  };

  const validateAndSetErrors = (validateUnedited, email, phone) => {
    let stateData = { ...userInfo };

    let _phoneEdited;
    let _emailEdited;

    // section where checks if fields are edited
    if ((phoneEdited || phone) && validateUnedited) {
      _phoneEdited = true;
    } else {
      _phoneEdited = !!(stateData.phone && stateData.confirmPhone);
    }

    if ((emailEdited || email) && validateUnedited) {
      _emailEdited = true;
    } else {
      _emailEdited = !!(stateData.email && stateData.confirmEmail);
    }

    // section where checks if there is errors in fields
    let validationErrors = {};

    // if noEmail is not checked and email field was edited
    if (!stateData.noEmail && _emailEdited) {
      Validation.validateEmailInAgreement(
        stateData.email,
        stateData.confirmEmail,
        validationErrors
      );
    }

    // if phonenumber field was edited
    if (_phoneEdited) {
      Validation.validatePhoneInAgreement(
        stateData.phone,
        stateData.confirmPhone,
        validationErrors
      );
    }

    // section where sets states and returns validation errors
    setErrors(validationErrors);
    setPhoneEdited(_phoneEdited);
    setEmailEdited(_emailEdited);

    return validationErrors;
  };

  const hideAgreementError = () => {
    setAgreementError(false);
  };

  const triggerCancelModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = (action) => {
    switch (action) {
      case 'abort':
        cancelAgreement();
        break;
      case 'cancel':
        setModalIsOpen(false);
        break;
    }
  };

  const handleUserInfoPhone = (event) => {
    if (event.target.name === 'phone') {
      setUserInfo({
        ...userInfo,
        phoneFieldEdited: true,
        confirmPhone: '',
      });
    }
    validateAndSetErrors(true, false, true);
  };

  const handleUserInfoEmail = () => {
    validateAndSetErrors(true, true, false);
  };

  const handleNoEmail = (event) => {
    // if "no email" is checked, reset email editing and error state
    if (event.target.checked === true) {
      setUserInfo({
        ...userInfo,
        noEmail: true,
        email: '',
        confirmEmail: '',
      });
      setErrors({
        ...errors,
        email: [],
        confirmEmail: [],
      });
      setEmailEdited(false);
    } else {
      setUserInfo({
        ...userInfo,
        noEmail: false,
      });
    }
  };

  const handleUserInfoChange = (event) => {
    // handle by type
    switch (event.target.type) {
      case 'checkbox': {
        setUserInfo({
          ...userInfo,
          [event.target.name]: event.target.checked,
        });
        if (event.target.name === 'noEmail') {
          handleNoEmail(event);
        }
        break;
      }
      default: {
        setUserInfo({
          ...userInfo,
          [event.target.name]: event.target.value,
        });
      }
    }
  };

  const completeAgreement = () => {
    let errors = validateAndSetErrors(true, true, true);
    let stateData = { ...agreementUserInfo, ...userInfo };

    if (
      isEmpty(errors.email) &&
      isEmpty(errors.phone) &&
      isEmpty(errors.confirmEmail) &&
      isEmpty(errors.confirmPhone)
    ) {
      setSendingAgreement(true);

      let agreementFormData = omit(stateData, ['SHIB_CONTEXT', 'hetu']);

      Api.saveUserAgreement(agreementFormData)
        .then(() => {
          // successful - log in

          if (WindowUtils.getFromSessionStorage(NAVIGATE_TO_AGREEMENT) != null
              && WindowUtils.getFromSessionStorage(POST_REGISTER_REDIRECT_URL_KEY) != null) {

            const redirectUrl = WindowUtils.getFromSessionStorage(POST_REGISTER_REDIRECT_URL_KEY);
            WindowUtils.addToSessionStorage(NAVIGATE_TO_AGREEMENT, "false");


            if (redirectUrl != null) {
              WindowUtils.refresh("#/" + redirectUrl);
            } else {
              WindowUtils.refresh(Urls.frontPage);
            }

          } else {
            // here we practically refresh, not navigate since
            // Urls.frontPage is a hash url
            WindowUtils.refresh(Urls.frontPage);
          }
        })
        .catch((err) => {
          setAgreementError(true);
          setSendingAgreement(false);
        });
    }
  };

  return (
    <div id="ot-mainview" className="ot-mainview span12">
      <MyErrorBoundary errorMessage="agreement.error" isHtml={true}>
        <Route
          exact
          path={`${props.match.url}`}
          render={() => <Redirect to={`${props.match.url}1`} />}
        />
        <Route
          path={`${props.match.url}1`}
          render={(props) => (
            <AgreementPage1
              {...props}
              userInfo={agreementUserInfo}
              triggerCancelModal={triggerCancelModal}
            />
          )}
        />
        <Route
          path={`${props.match.url}2`}
          render={(props) => (
            <AgreementPage2
              {...props}
              userInfo={agreementUserInfo}
              triggerCancelModal={triggerCancelModal}
            />
          )}
        />
        <Route
          path={`${props.match.url}3`}
          render={(props) => (
            <AgreementPage3
              {...props}
              userInfo={agreementUserInfo}
              triggerCancelModal={triggerCancelModal}
            />
          )}
        />
        <Route
          path={`${props.match.url}4`}
          render={(props) => (
            <AgreementPage4
              {...props}
              userInfo={agreementUserInfo}
              triggerCancelModal={triggerCancelModal}
            />
          )}
        />
        <Route
          path={`${props.match.url}5`}
          render={(props) => (
            <AgreementPage5
              {...props}
              hideActivationError={hideAgreementError}
              agreementError={agreementError}
              sendingAgreement={sendingAgreement}
              errors={errors}
              userInfo={{ ...agreementUserInfo, ...userInfo }}
              triggerCancelModal={triggerCancelModal}
              handleUserInfoChange={handleUserInfoChange}
              handleUserInfoPhone={handleUserInfoPhone}
              handleUserInfoEmail={handleUserInfoEmail}
              completeAgreement={completeAgreement}
            />
          )}
        />
        <AgreementCancelModal isOpen={modalIsOpen} closeModal={closeModal} />
      </MyErrorBoundary>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    isHpa: state.hpa.isHpa,
    applicationState: state[APPLICATION].applicationState,
    loggedIn: state[USER].userInfo.loggedIn,
    isUser: state[USER].userInfo.isUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    doExitHpa: () => dispatch(doExitHpa()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Agreement);
